import type { Element } from 'hast';
import * as React from 'react';
import Markdown, { Components, defaultUrlTransform } from 'react-markdown';
import type { Root } from 'react-markdown/lib';
import remarkGfm from 'remark-gfm';
import { visit } from 'unist-util-visit';

import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { VoiceboxPanelLink } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelLink';
import { VoiceboxApp } from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Link } from 'bones/design-system/components/Link';
import { generateExplorerLink } from 'bones/utils/generateExplorerLink';
import { usePortalDashboardUrl } from 'bones/utils/portal/usePortalDashboardUrl';

const VoiceboxInternalLink: React.VFC<React.HTMLProps<HTMLAnchorElement>> = ({
  children,
  // @ts-ignore
  'data-iri': iri,
  // @ts-ignore
  'data-messageid': messageId,
  // @ts-ignore
  'data-testid': testId,
}) => (
  <VoiceboxPanelLink
    iri={iri}
    keepHistory={false}
    messageId={messageId}
    tagName="span"
    testId={testId}
  >
    {children}
  </VoiceboxPanelLink>
);

const VoiceboxExternalLink: React.VFC<React.HTMLProps<HTMLAnchorElement>> = ({
  children,
  // @ts-ignore
  'data-testid': testId,
  ...linkProps
}) => (
  <Link
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...linkProps}
    role="button"
    size="md"
    tabIndex={0}
    testId={testId}
  >
    {children}
  </Link>
);

const VoiceboxDesignerLink: React.VFC<React.HTMLProps<HTMLAnchorElement>> = ({
  children,
  // @ts-ignore
  'data-iri': iri,
  // @ts-ignore
  'data-testid': testId,
  ...linkProps
}) => {
  const {
    links: { onClickIri },
  } = useVoiceboxSchema();

  const handleClickIri = onClickIri?.(iri);
  if (!handleClickIri) {
    return <span>{children}</span>;
  }

  return (
    <Link
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...linkProps}
      onClick={(evt) => {
        evt.preventDefault();
        handleClickIri(iri);
      }}
      role="button"
      size="md"
      tabIndex={0}
      testId={testId}
    >
      {children}
    </Link>
  );
};

export interface VoiceboxMarkdownProps {
  linkTestId?: (iri: string, linkIndex: number) => string;
  markdown: string;
  messageId: string;
  settings: VoiceboxSettings;
}

export const VoiceboxMarkdown: React.VFC<VoiceboxMarkdownProps> = ({
  linkTestId,
  markdown,
  messageId,
  settings,
}) => {
  const {
    config,
    links: { openLinksInNewTab = true, openLinksInPanel },
  } = useVoiceboxSchema();
  const { conversationId } = useVoiceboxConversation();

  const dashboardUrl = usePortalDashboardUrl(config.connectionIdx);
  const isDesigner = config.app === VoiceboxApp.DESIGNER;

  const explorerUrlArgs = dashboardUrl
    ? {
        conversationId,
        dashboardUrl,
        databaseId: settings.databaseId,
        model: settings.model,
        namedGraphs: settings.namedGraphs,
        reasoning: settings.reasoning,
      }
    : null;

  const plugin = () => (tree: Root) => {
    let linkIndex = 0;
    visit(tree, 'element', (node, index, parent) => {
      if (node.tagName === 'a') {
        const iri = node.properties.href as string | undefined;
        // istanbul ignore if
        if (!iri) {
          // eslint-disable-next-line no-param-reassign
          node.tagName = 'span';
          // eslint-disable-next-line no-param-reassign
          node.properties = {};
        } else if (openLinksInPanel || isDesigner) {
          // eslint-disable-next-line no-param-reassign
          node.properties = {
            ...node.properties,
            'data-iri': iri,
            'data-messageid': messageId,
            'data-testid': linkTestId?.(iri, linkIndex),
          };
        } else {
          const href = explorerUrlArgs
            ? defaultUrlTransform(
                generateExplorerLink({ ...explorerUrlArgs, iri })
              )
            : '';

          if (href) {
            // eslint-disable-next-line no-param-reassign
            node.properties = {
              ...node.properties,
              'data-testid': linkTestId?.(iri, linkIndex),
              href,
              rel: 'noreferrer',
              target: openLinksInNewTab ? '_blank' : '_self',
            };
          } else {
            // eslint-disable-next-line no-param-reassign
            node.tagName = 'span';
            // eslint-disable-next-line no-param-reassign
            node.properties = {};
          }
        }

        linkIndex += 1;
      } else if (
        node.tagName === 'table' &&
        parent &&
        typeof index === 'number'
      ) {
        // eslint-disable-next-line no-param-reassign
        node.properties.className = [
          'vb-data-table-body',
          'vb-data-table-body-size--md',
        ];

        const innerDiv: Element = {
          type: 'element',
          tagName: 'div',
          properties: {
            className: ['vb-data-table'],
          },
          children: [node],
        };

        const outerDiv: Element = {
          type: 'element',
          tagName: 'div',
          properties: {
            className: ['vb-voicebox-csv-table'],
          },
          children: [innerDiv],
        };

        // eslint-disable-next-line no-param-reassign
        parent.children[index] = outerDiv;
      } else if (node.tagName === 'th') {
        // just the way we do things
        // eslint-disable-next-line no-param-reassign
        node.tagName = 'td';
      } else if (node.tagName === 'td') {
        // library seems to add padding-left
        // eslint-disable-next-line no-param-reassign
        node.properties = {};
      }
    });
  };

  let components: Partial<Components>;
  if (isDesigner) {
    components = { a: VoiceboxDesignerLink };
  } else if (openLinksInPanel) {
    components = { a: VoiceboxInternalLink };
  } else {
    components = { a: VoiceboxExternalLink };
  }

  return (
    <Markdown
      className="vb-voicebox-markdown"
      components={components}
      rehypePlugins={[plugin]}
      remarkPlugins={[remarkGfm]}
    >
      {markdown}
    </Markdown>
  );
};
